import { useId, useState, type CSSProperties, type KeyboardEvent, type WheelEvent } from "react";
import {
  clampRunningPaceSecPerMi,
  formatRunningPace,
  isValidRunningPaceSecPerMi,
  RUNNING_PACE_STEP_SEC,
  secPerKmToSecPerMi,
  stepRunningPaceSecPerMi
} from "@shared/modules/preferences/runningPace";
import type { DistanceUnit } from "../../lib/units";
import "../Planner/addWorkoutV4.css";
import "../modals/modals.css";

/** Connect / reconnect gate — includes where to edit later. */
export const RUNNING_PACE_HINT_CONNECT =
  "This helps us fuel you better when you connect your training apps. If you aren't sure, around 10 minutes per mile is a good starting point for most people. You can change this anytime in your Fueling profile preferences.";

/** Fueling profile — same guidance, without the “change in preferences” line. */
export const RUNNING_PACE_HINT_PREFERENCES =
  "This helps us fuel you better when you connect your training apps. If you aren't sure, around 10 minutes per mile is a good starting point for most people.";

/** Quick chips — minutes in the athlete's unit (5 / 7 / 10 / 12 / 15 / 20). */
const PACE_QUICK_CHIPS_MIN = [5, 7, 10, 12, 15, 20] as const;

function minutesChipToSecPerMi(minutes: number, unit: DistanceUnit): number {
  const displaySec = minutes * 60;
  const secPerMi = unit === "km" ? secPerKmToSecPerMi(displaySec) : displaySec;
  return clampRunningPaceSecPerMi(secPerMi);
}

function chipSelected(valueSecPerMi: number | null, minutes: number, unit: DistanceUnit): boolean {
  if (valueSecPerMi == null) return false;
  return valueSecPerMi === minutesChipToSecPerMi(minutes, unit);
}

const stepBtnStyle: CSSProperties = {
  appearance: "none",
  border: "none",
  cursor: "pointer",
  width: 30,
  height: 30,
  borderRadius: "var(--fg-r-pill)",
  background: "var(--fg-gray-100)",
  color: "var(--fg-ink)",
  fontFamily: "var(--fg-display)",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: 1,
  display: "inline-grid",
  placeItems: "center"
};

/**
 * Pace control: quick chips + `− value +` stepper. No typing — nudge with
 * ±, arrow keys, or scroll on the value. Stored as seconds per mile.
 */
export function RunningPaceField({
  valueSecPerMi,
  unit,
  onCommit,
  onValidDraft,
  disabled = false,
  hideLabel = false,
  id: idProp,
  hint
}: {
  valueSecPerMi: number | null;
  unit: DistanceUnit;
  onCommit: (secPerMi: number | null) => void | Promise<void>;
  onValidDraft?: (secPerMi: number | null) => void;
  disabled?: boolean;
  /** Kept for call-site compatibility; gating is owned by the parent. */
  required?: boolean;
  hideLabel?: boolean;
  id?: string;
  hint?: string | null;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const display =
    valueSecPerMi != null ? formatRunningPace(valueSecPerMi, unit) : "Set pace";

  const apply = async (secPerMi: number) => {
    const next = clampRunningPaceSecPerMi(secPerMi);
    if (!isValidRunningPaceSecPerMi(next)) {
      setError("Pick a pace in range");
      onValidDraft?.(null);
      return;
    }
    setError(null);
    onValidDraft?.(next);
    setSaving(true);
    try {
      await onCommit(next);
    } finally {
      setSaving(false);
    }
  };

  const step = (dir: 1 | -1) => {
    if (disabled || saving) return;
    void apply(stepRunningPaceSecPerMi(valueSecPerMi, dir, unit, RUNNING_PACE_STEP_SEC));
  };

  const onValueKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    }
  };

  const onValueWheel = (e: WheelEvent<HTMLButtonElement>) => {
    if (disabled || saving) return;
    e.preventDefault();
    step(e.deltaY > 0 ? 1 : -1);
  };

  return (
    <div className={`fg-running-pace${hideLabel ? " fg-running-pace--nolabel" : ""}`}>
      <div className="fg-running-pace__label" id={id}>
        Average running pace
      </div>
      <div className="fgp-col-gap10" role="group" aria-labelledby={id}>
        <div className="fgp-awv4-durchips">
          {PACE_QUICK_CHIPS_MIN.map((m) => {
            const on = chipSelected(valueSecPerMi, m, unit);
            return (
              <button
                type="button"
                key={m}
                onClick={() => void apply(minutesChipToSecPerMi(m, unit))}
                aria-pressed={on}
                disabled={disabled || saving}
                className={`fgp-awv4-durchip${on ? " fgp-awv4-durchip--on" : ""}`}>
                {m}:00
              </button>
            );
          })}
        </div>

        <div className="fg-running-pace__dur">
          <div className="fg-running-pace__shell">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Faster pace"
              disabled={disabled || saving}
              style={stepBtnStyle}>
              −
            </button>
            <button
              type="button"
              onClick={() => {
                // Unset → seed a starting pace so ± / scroll / arrows have something to move.
                if (valueSecPerMi == null) step(1);
              }}
              onKeyDown={onValueKeyDown}
              onWheel={onValueWheel}
              disabled={disabled || saving}
              className="fg-running-pace__value-btn"
              title={disabled ? undefined : "Use − / + , arrow keys, or scroll to adjust"}
              aria-label={`Average running pace ${display} per ${unit}. Use plus and minus or arrow keys to adjust.`}>
              {display}
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Slower pace"
              disabled={disabled || saving}
              style={stepBtnStyle}>
              +
            </button>
          </div>
          <span className="fg-running-pace__unit">/{unit}</span>
        </div>
      </div>
      {hint ? <p className="fg-running-pace__hint">{hint}</p> : null}
      {error ? (
        <p className="fg-running-pace__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Read-only pace line for connection detail. */
export function RunningPaceReadOnly({
  valueSecPerMi,
  unit,
  loading = false
}: {
  valueSecPerMi: number | null;
  unit: DistanceUnit;
  /** While preferences are still loading — avoid flashing "Not set". */
  loading?: boolean;
}) {
  const display = loading
    ? "…"
    : valueSecPerMi != null
      ? `${formatRunningPace(valueSecPerMi, unit)} /${unit}`
      : "Not set";
  return (
    <div className="fg-running-pace fg-running-pace--readonly">
      <span className="fg-running-pace__label">Average running pace</span>
      <span className="fg-running-pace__value">{display}</span>
      <p className="fg-running-pace__hint">You can edit this in your Fueling profile preferences.</p>
    </div>
  );
}
